import { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { TFunction } from 'i18next';
import QR from '../atoms/QR/QR';
import { copyToClipboard } from '../../services/clipboard';
import { CHAT, money } from './tokens';
import type { ChatIssuance } from '../../services/conciergeChatTypes';

const Card = styled.div<{ $dim: boolean }>`
  align-self: stretch;
  max-width: 420px;
  padding: 12px 14px 14px;
  border-radius: 16px;
  background: ${({ $dim }) => ($dim ? CHAT.grey : CHAT.surface)};
`;
const Label = styled.div`
  font-size: 12px;
  color: ${CHAT.muted};
`;
const Bank = styled.div`
  font-size: 13px;
  margin-bottom: 8px;
`;
const Warn = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${CHAT.warn};
  margin-bottom: 6px;
`;
const NumberText = styled.div<{ $dim: boolean }>`
  font-family: ${CHAT.mono};
  font-weight: 700;
  font-size: ${({ $dim }) => ($dim ? '16px' : '18px')};
  color: ${({ $dim }) => ($dim ? '#a8a8a8' : CHAT.ink)};
  ${({ $dim }) => ($dim ? 'text-decoration: line-through;' : '')}
  word-break: break-all;
`;
const Amount = styled.div`
  font-family: ${CHAT.mono};
  font-size: 22px;
  font-weight: 700;
`;
const Divider = styled.div`
  height: 1px;
  margin: 10px 0;
  background: ${CHAT.line};
`;
const Copy = styled.button<{ $done: boolean }>`
  margin-top: 6px;
  width: 100%;
  min-height: 38px;
  border: 1px solid ${({ $done }) => ($done ? CHAT.action : 'rgba(106, 170, 58, 0.45)')};
  border-radius: 10px;
  background: ${({ $done }) => ($done ? CHAT.action : '#fff')};
  color: ${({ $done }) => ($done ? '#fff' : CHAT.actionText)};
  font-family: ${CHAT.sans};
  font-size: 13px;
  cursor: pointer;
`;
const Pay = styled.a`
  display: block;
  margin-top: 10px;
  padding: 12px;
  border-radius: 12px;
  background: ${CHAT.paid};
  color: #fff;
  text-align: center;
  text-decoration: none;
  font-size: 15px;
  font-weight: 600;
`;
const Timer = styled.div`
  margin-top: 10px;
  font-size: 13px;
  color: ${CHAT.muted};
  span {
    margin-left: 4px;
    font-family: ${CHAT.mono};
    font-weight: 700;
    color: ${CHAT.actionText};
  }
`;
const QrCenter = styled.div`
  display: flex;
  justify-content: center;
  margin: 6px 0;
`;

const left = (deadline: string | null): number | null => {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  return Number.isNaN(ms) ? null : Math.max(0, Math.floor(ms / 1000));
};
const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const CopyButton = ({ value, label, done }: { value: string; label: string; done: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Copy
      type='button'
      $done={copied}
      onClick={async () => {
        if (await copyToClipboard(value.replace(/\s/g, ''))) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }
      }}
    >
      {copied ? done : label}
    </Copy>
  );
};

/**
 * One issuance as a card. Only the active one carries buttons and the timer, and only until the
 * client sent a receipt; a cancelled one, and one of another deposit of the player, keep their number struck
 * through so the client recognises where not to pay.
 */
const IssuanceCard = ({ issuance, receiptSent, deadlineAt, t }: { issuance: ChatIssuance; receiptSent: boolean; deadlineAt: string | null; t: TFunction }) => {
  const live = issuance.state === 'active' && !receiptSent;
  const [seconds, setSeconds] = useState(() => left(deadlineAt));

  useEffect(() => {
    if (!live) return undefined;
    setSeconds(left(deadlineAt));
    const id = setInterval(() => setSeconds(left(deadlineAt)), 1000);
    return () => clearInterval(id);
  }, [live, deadlineAt]);

  const dim = issuance.state === 'cancelled' || issuance.state === 'expired' || issuance.state === 'used';
  const bank = [issuance.bank, issuance.holder ? t('concierge.chat.card.holder', { name: issuance.holder }) : null]
    .filter(Boolean)
    .join(' · ');
  const label =
    issuance.kind === 'link'
      ? t('concierge.chat.card.link')
      : issuance.kind === 'qr'
        ? t('concierge.chat.card.qr')
        : issuance.channel
          ? t(`concierge.chat.card.${issuance.channel}`)
          : t('concierge.chat.card.requisite');

  return (
    <Card $dim={dim} data-testid={`chat-card-${issuance.state}`}>
      {issuance.state === 'cancelled' && <Warn>{t('concierge.chat.card.cancelled')}</Warn>}
      <Label>
        {label}
        {issuance.state === 'expired' && ` · ${t('concierge.chat.card.expired')}`}
        {issuance.state === 'paid' && ` · ${t('concierge.chat.card.paid')}`}
        {issuance.state === 'used' && ` · ${t('concierge.chat.card.used')}`}
      </Label>
      {bank && <Bank>{bank}</Bank>}

      {issuance.kind === 'requisite' && issuance.number && (
        <>
          <NumberText $dim={dim}>{issuance.number}</NumberText>
          {live && <CopyButton value={issuance.number} label={t('concierge.chat.card.copyNumber')} done={t('concierge.chat.card.copied')} />}
        </>
      )}

      {issuance.kind === 'qr' && issuance.paymentUrl && live && (
        <QrCenter>
          <QR data={issuance.paymentUrl} size={140} bgColor='#ffffff' fgColor='#111111' frameColor='#ffffff' />
        </QrCenter>
      )}

      {issuance.amount !== null && !dim && (
        <>
          <Divider />
          <Label>{t('concierge.chat.card.amount')}</Label>
          <Amount>{money(issuance.amount)}</Amount>
          {live && issuance.kind === 'requisite' && (
            <CopyButton value={String(issuance.amount)} label={t('concierge.chat.card.copyAmount')} done={t('concierge.chat.card.copied')} />
          )}
        </>
      )}

      {live && issuance.kind !== 'requisite' && issuance.paymentUrl && (
        <Pay href={issuance.paymentUrl} target='_blank' rel='noopener noreferrer'>
          {issuance.kind === 'qr' ? t('concierge.chat.card.qrHint') : t('concierge.chat.card.pay')}
        </Pay>
      )}

      {live && seconds !== null && (
        <Timer>
          {issuance.kind === 'requisite' ? t('concierge.chat.card.awaiting') : t('concierge.chat.card.linkValid')}
          <span>{clock(seconds)}</span>
        </Timer>
      )}
    </Card>
  );
};

export default IssuanceCard;
